import { useMemo } from 'react'

import { type AccessOptions, hasAccess, isSysOp, isSysAdmin } from '@/lib/auth/permissions'
import { type UserSection } from '@/models/domain/user'
import { useAuthStore } from '@/stores/auth-store'

export function usePermissions() {
  const { user } = useAuthStore()

  return {
    user,
    isSysOp: user ? isSysOp(user) : false,
    isSysAdmin: user ? isSysAdmin(user) : false,
    hasAccess: (sections: UserSection[], options?: AccessOptions) =>
      user ? hasAccess(user, sections, options) : false,
  }
}

/**
 * Returns true when the current user holds the given write permission.
 *
 * Use in components that already passed a read-level route guard and now
 * need to decide whether to enable Create / Edit / Save / Delete affordances.
 * Pass `{ strict: true }` for platform-level write checks where SysAdmin
 * must hold the permission explicitly.
 *
 * Section check only — does not re-verify account activeness. The route guard
 * already ran the full `checkPermission` chain; api-core enforces the final
 * boundary on the write request.
 */
export function useWriteAccess(writePerm: UserSection, options?: AccessOptions): boolean {
  const { user } = useAuthStore()
  const strict = options?.strict
  return useMemo(
    () => (user ? hasAccess(user, [writePerm], { strict }) : false),
    [user, writePerm, strict]
  )
}
